import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Alert, Button } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaFilter, FaTimes, FaUndo } from 'react-icons/fa';
import {
  listarGruposGeListaAtivos,
  listarMoedasListaAtivos,
} from '~/Services/ListaAtivosService';
import { listarUltimasCotacoes } from '~/Services/Relatorios/RelatorioCotacaoMoedaForteService';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import './sidebarFiltros.css';

const selectStylesSidebar = {
  ...selectStylesSm,
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const TIPOS_ATIVO = [
  { value: '0', label: 'Moeda' },
  { value: '1', label: 'Imobilizado' },
  { value: '2', label: 'Pedra' },
  { value: '3', label: 'Mercadoria' },
  { value: '4', label: 'Imóvel' },
  { value: '5', label: 'Animal' },
];

const STATUS_OPCOES = [
  { value: '0', label: 'Ativos' },
  { value: '1', label: 'Inativos' },
  { value: null, label: 'Ambos' },
];

const MODO_OPCOES = [
  { value: 0, label: 'Com saldo' },
  { value: 1, label: 'Todos' },
  { value: 2, label: 'Custo médio = 0' },
];

const SidebarFiltros = ({ formik, isOpen, onClose }) => {
  const [gruposOpcoes, setGruposOpcoes] = useState([]);
  const [moedasOpcoes, setMoedasOpcoes] = useState([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(true);

  // Carrega Grupos e Moedas no mount (GE migrou para o HeaderAzul)
  useEffect(() => {
    let ativo = true;

    const carregarDados = async () => {
      const [resGrupos, resMoedas] = await Promise.allSettled([
        listarGruposGeListaAtivos(),
        listarMoedasListaAtivos(),
      ]);

      if (!ativo) return;

      const erros = [];

      if (resGrupos.status === 'fulfilled') {
        const grupos = (resGrupos.value || []).map((g) => ({
          value: g.codigo,
          label: g.descricao,
        }));
        setGruposOpcoes(grupos);
      } else {
        erros.push(extrairMensagemErro(resGrupos.reason));
      }

      if (resMoedas.status === 'fulfilled') {
        const moedas = (resMoedas.value || []).map((m) => ({
          value: m.codigo_ativo,
          label: m.nome,
        }));
        setMoedasOpcoes(moedas);
      } else {
        erros.push(extrairMensagemErro(resMoedas.reason));
      }

      setLoadingOpcoes(false);

      if (erros.length > 0) {
        toast.error(erros[0]);
      }
    };

    carregarDados();
    return () => {
      ativo = false;
    };
  }, []);

  // Carrega cotações quando "Converter" é marcado (ADR-002 + reuso W-0582)
  const handleConversaoChange = async (checked) => {
    formik.setFieldValue('conversaoMoeda', checked);
    if (!checked) {
      formik.setFieldValue('cotacoes', { usd: 0, eur: 0, gbp: 0 });
      formik.setFieldValue('moedaCodigo', null);
      return;
    }
    try {
      const [resUsd, resEur, resGbp] = await listarUltimasCotacoes();
      const usd = resUsd && resUsd.linhas && resUsd.linhas.length > 0
        ? resUsd.linhas[resUsd.linhas.length - 1].cotacao
        : 0;
      const eur = resEur && resEur.linhas && resEur.linhas.length > 0
        ? resEur.linhas[resEur.linhas.length - 1].cotacao
        : 0;
      const gbp = resGbp && resGbp.linhas && resGbp.linhas.length > 0
        ? resGbp.linhas[resGbp.linhas.length - 1].cotacao
        : 0;
      formik.setFieldValue('cotacoes', { usd, eur, gbp });
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  // Exclusão mútua GE / Grupo de GE (RN-10) — GE é controlado no HeaderAzul
  const handleGrupoChange = (opcao) => {
    formik.setFieldValue('grupoGeId', opcao ? opcao.value : null);
    formik.setFieldValue('geId', null);
  };

  const handleTipoChange = (value, checked) => {
    const atual = formik.values.tipos;
    const novo = checked ? [...atual, value] : atual.filter((v) => v !== value);
    formik.setFieldValue('tipos', novo);
  };

  const handleLimparFiltros = () => {
    formik.setFieldValue('geId', null);
    formik.setFieldValue('grupoGeId', null);
    formik.setFieldValue('tipos', ['0', '1', '2', '3', '4', '5']);
    formik.setFieldValue('status', '0');
    formik.setFieldValue('modoListagem', 0);
    formik.setFieldValue('exibirImobilizado', false);
    formik.setFieldValue('conversaoMoeda', false);
    formik.setFieldValue('moedaCodigo', null);
    formik.setFieldValue('cotacoes', { usd: 0, eur: 0, gbp: 0 });
    formik.setErrors({});
  };

  const grupoValor =
    gruposOpcoes.find((g) => g.value === formik.values.grupoGeId) || null;
  const moedaValor =
    moedasOpcoes.find((m) => m.value === formik.values.moedaCodigo) || null;

  const erroCotacoes =
    formik.errors.cotacoes &&
    (typeof formik.errors.cotacoes === 'string'
      ? formik.errors.cotacoes
      : formik.errors.cotacoes.usd ||
        formik.errors.cotacoes.eur ||
        formik.errors.cotacoes.gbp);

  const errosConsolidados = [
    formik.errors['escopo-obrigatorio'],
    formik.errors.tipos,
    formik.errors.moedaCodigo,
    erroCotacoes,
  ].filter(Boolean);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="lista-ativos-sidebar-overlay"
        onClick={onClose}
        role="presentation"
      />
      <aside className="lista-ativos-sidebar" role="dialog" aria-label="Filtros">
        <div className="lista-ativos-sidebar__header">
          <h5>
            <FaFilter className="mr-2" />
            Filtros
          </h5>
          <button
            type="button"
            className="lista-ativos-sidebar__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <FaTimes />
          </button>
        </div>

        <div className="lista-ativos-sidebar__content">
          {errosConsolidados.length > 0 && (
            <Alert variant="danger" className="py-2 small">
              {errosConsolidados.map((msg, idx) => (
                <div key={idx}>{msg}</div>
              ))}
            </Alert>
          )}

          {/* Escopo */}
          <div className="lista-ativos-sidebar__section">
            <span className="lista-ativos-sidebar__section-title">Escopo</span>
            <Row>
              <Col md={12}>
                <Form.Label className="small mb-1">Grupo de GE</Form.Label>
                <Select
                  styles={selectStylesSidebar}
                  menuPortalTarget={document.body}
                  options={gruposOpcoes}
                  value={grupoValor}
                  onChange={handleGrupoChange}
                  isClearable
                  isLoading={loadingOpcoes}
                  loadingMessage={() => 'Carregando...'}
                  noOptionsMessage={() => 'Nenhum registro disponível'}
                  placeholder="Selecione um grupo..."
                />
              </Col>
            </Row>
          </div>

          {/* Tipos de Ativo */}
          <div className="lista-ativos-sidebar__section">
            <span className="lista-ativos-sidebar__section-title">
              Tipos de Ativo
            </span>
            <Row>
              {TIPOS_ATIVO.map((tipo) => (
                <Col xs={6} key={tipo.value} className="mb-2">
                  <Form.Check
                    type="switch"
                    id={`switch-tipo-${tipo.value}`}
                    label={tipo.label}
                    checked={formik.values.tipos.includes(tipo.value)}
                    onChange={(e) =>
                      handleTipoChange(tipo.value, e.target.checked)
                    }
                  />
                </Col>
              ))}
            </Row>
          </div>

          {/* Status */}
          <div className="lista-ativos-sidebar__section">
            <span className="lista-ativos-sidebar__section-title">Status</span>
            <Row>
              {STATUS_OPCOES.map((op) => (
                <Col xs={6} key={String(op.value)} className="mb-2">
                  <Form.Check
                    type="radio"
                    id={`status-${op.value}`}
                    label={op.label}
                    name="status"
                    checked={formik.values.status === op.value}
                    onChange={() => formik.setFieldValue('status', op.value)}
                  />
                </Col>
              ))}
            </Row>
          </div>

          {/* Modo */}
          <div className="lista-ativos-sidebar__section">
            <span className="lista-ativos-sidebar__section-title">
              Modo de listagem
            </span>
            <Row>
              {MODO_OPCOES.map((op) => (
                <Col xs={6} key={op.value} className="mb-2">
                  <Form.Check
                    type="radio"
                    id={`modo-${op.value}`}
                    label={op.label}
                    name="modoListagem"
                    checked={formik.values.modoListagem === op.value}
                    onChange={() =>
                      formik.setFieldValue('modoListagem', op.value)
                    }
                  />
                </Col>
              ))}
            </Row>
          </div>

          {/* Opções adicionais */}
          <div className="lista-ativos-sidebar__section">
            <span className="lista-ativos-sidebar__section-title">
              Opções adicionais
            </span>
            <Form.Check
              type="switch"
              id="switch-exibir-imobilizado"
              label="Exibir Código Imobilizado"
              checked={formik.values.exibirImobilizado}
              onChange={(e) =>
                formik.setFieldValue('exibirImobilizado', e.target.checked)
              }
              className="mb-2"
            />
            <Form.Check
              type="switch"
              id="switch-conversao-moeda"
              label="Converter para moeda forte"
              checked={formik.values.conversaoMoeda}
              onChange={(e) => handleConversaoChange(e.target.checked)}
            />
          </div>

          {/* Conversão de Moeda */}
          {formik.values.conversaoMoeda && (
            <div className="lista-ativos-sidebar__section">
              <span className="lista-ativos-sidebar__section-title">
                Conversão de Moeda
              </span>
              <Row>
                <Col xs={12} className="mb-3">
                  <Form.Label className="small mb-1">Moeda</Form.Label>
                  <Select
                    styles={selectStylesSidebar}
                    menuPortalTarget={document.body}
                    options={moedasOpcoes}
                    value={moedaValor}
                    onChange={(op) =>
                      formik.setFieldValue('moedaCodigo', op ? op.value : null)
                    }
                    isClearable
                    isLoading={loadingOpcoes}
                    loadingMessage={() => 'Carregando...'}
                    noOptionsMessage={() => 'Nenhum registro disponível'}
                    placeholder="Selecione..."
                  />
                </Col>
                <Col xs={4} className="mb-2">
                  <Form.Label className="small mb-1">USD (1)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    size="sm"
                    value={formik.values.cotacoes.usd}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'cotacoes.usd',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </Col>
                <Col xs={4} className="mb-2">
                  <Form.Label className="small mb-1">EUR (2)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    size="sm"
                    value={formik.values.cotacoes.eur}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'cotacoes.eur',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </Col>
                <Col xs={4} className="mb-2">
                  <Form.Label className="small mb-1">GBP (10)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    size="sm"
                    value={formik.values.cotacoes.gbp}
                    onChange={(e) =>
                      formik.setFieldValue(
                        'cotacoes.gbp',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </Col>
              </Row>
            </div>
          )}
        </div>

        <div className="lista-ativos-sidebar__footer">
          <Button
            variant="outline-secondary"
            size="sm"
            className="flex-fill"
            onClick={handleLimparFiltros}
          >
            <FaUndo className="mr-1" />
            Limpar
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="flex-fill"
            onClick={onClose}
          >
            Aplicar
          </Button>
        </div>
      </aside>
    </>
  );
};

export default SidebarFiltros;
